import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowUpCircle, Clock } from 'lucide-react';
import { compareVersions, LOCK_MESSAGES, UPDATE_NOTICE_HOURS, updateNoticeText, type ScheduledUpdateDto } from '@his/shared';

const HOUR_MS = 60 * 60 * 1000;

function scheduledNotice(scheduled: ScheduledUpdateDto | null, now: number = Date.now()): string | null {
  if (!scheduled) return null;
  const at = new Date(scheduled.at);
  const until = at.getTime() - now;
  if (until < -15 * 60 * 1000 || until > UPDATE_NOTICE_HOURS * HOUR_MS) return null;
  return updateNoticeText(at, (value) => value.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
}

function requiredDeadline(requiredBy: string | null, pending: boolean, now: number = Date.now()): string | null {
  if (!requiredBy || !pending) return null;
  const days = Math.ceil((Date.parse(requiredBy) - now) / (24 * HOUR_MS));
  if (days <= 0) return 'Required now — it installs itself at 02:00';
  return `Required within ${days} day${days === 1 ? '' : 's'}`;
}
import { onlineCopyBannerLines } from '@/lib/online-copy';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { ABOUT_SETTINGS_PATH, useUpdateStatusQuery } from '@/lib/updates-api';
import { ReleaseNotesDialog, UpdateApplyDialog } from './update-flow';

/**
 * Offline distribution (plan §3.3): the non-blocking strip an administrator
 * sees on a local install when a newer version is known — "Version X
 * available · View notes · Update now". Red when the running version is
 * below the owner's minimum (the install is already read-only, see
 * LicenseLockBanner). Amber, with a link to the About tab, when the last
 * update attempt failed and was rolled back. Nothing on the cloud, and
 * nothing for anyone who could not start the update anyway.
 */
export function UpdateBanner() {
  const { user, can } = useAuth();
  const onInstall = Boolean(user && !user.isPlatformUser && user.deploymentMode === 'LOCAL' && user.readOnlyReason !== 'cloud_mirror');
  const isAdmin = onInstall && can('tenant.settings.manage');
  const statusQuery = useUpdateStatusQuery({ enabled: onInstall });
  const [notesOpen, setNotesOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);

  const status = statusQuery.data;
  if (!onInstall || !status || status.deploymentMode !== 'LOCAL') return null;
  const notice = scheduledNotice(status.scheduledUpdate ?? null);
  if (notice) {
    return (
      <div role="alert" className="flex items-center justify-center gap-2 border-b border-warning/30 bg-warning/10 px-4 py-2 text-xs font-medium text-warning no-print">
        <Clock className="h-3.5 w-3.5 flex-none" aria-hidden />
        {notice}
      </div>
    );
  }
  if (!isAdmin) return null;
  const deadline = requiredDeadline(status.requiredBy ?? null, Boolean(status.pendingUpdate));

  const pending = status.pendingUpdate;
  if (!pending) {
    if (!status.lastUpdateFailed) {
      const lines = status.syncHealth ? onlineCopyBannerLines(status.syncHealth) : [];
      if (lines.length === 0) return null;
      return (
        <div role="status" className="flex items-center justify-center gap-2 border-b border-warning/30 bg-warning/10 px-4 py-2 text-xs font-medium text-warning no-print">
          <AlertTriangle className="h-3.5 w-3.5 flex-none" aria-hidden />
          {lines.join(' ')}
          <Link to={ABOUT_SETTINGS_PATH} className="underline underline-offset-2 hover:text-foreground">
            See details
          </Link>
        </div>
      );
    }
    return (
      <div role="status" className="flex items-center justify-center gap-2 border-b border-warning/30 bg-warning/10 px-4 py-2 text-xs font-medium text-warning no-print">
        <AlertTriangle className="h-3.5 w-3.5 flex-none" aria-hidden />
        The last update did not complete and was rolled back.
        <Link to={ABOUT_SETTINGS_PATH} className="underline underline-offset-2 hover:text-foreground">
          See details
        </Link>
      </div>
    );
  }

  const blocked = pending.isRequired && pending.minimumAllowedVersion !== null && compareVersions(status.runningVersion, pending.minimumAllowedVersion) < 0;

  return (
    <>
      <div
        role={blocked ? 'alert' : 'status'}
        className={cn(
          'flex items-center justify-center gap-3 border-b px-4 py-2 text-xs font-medium no-print',
          blocked ? 'border-destructive/30 bg-destructive/10 text-destructive' : 'border-primary/30 bg-primary/10 text-primary',
        )}
      >
        <ArrowUpCircle className="h-3.5 w-3.5 flex-none" aria-hidden />
        <span>{blocked ? LOCK_MESSAGES.VERSION : `Version ${pending.version} available`}</span>
        {deadline && !blocked && (
          <>
            <span className="opacity-60">·</span>
            <span>{deadline}</span>
          </>
        )}
        <span className="opacity-60">·</span>
        <button type="button" className="underline underline-offset-2 hover:text-foreground" onClick={() => setNotesOpen(true)}>
          View notes
        </button>
        <span className="opacity-60">·</span>
        <button type="button" className="font-semibold underline underline-offset-2 hover:text-foreground" onClick={() => setApplyOpen(true)}>
          Update now
        </button>
      </div>
      {notesOpen && (
        <ReleaseNotesDialog version={pending.version} notes={pending.notes} open={notesOpen} onOpenChange={setNotesOpen} />
      )}
      <UpdateApplyDialog status={status} open={applyOpen} onOpenChange={setApplyOpen} />
    </>
  );
}
